import { useState, useEffect, useCallback, useRef } from "react";
import { CalendarDays, Repeat2, Zap, TrendingUp, ChevronLeft, ChevronRight, ChevronDown, Search, RefreshCw, X, Hash, User, Shield } from "lucide-react";
import axiosInstance from "../../API/interceptor";

/* ══ CONSTANTS ═══════════════════════════════════════════════ */
const COLORS = {
  Daily:   { from:"#6D28D9", to:"#7C3AED", soft:"#F5F3FF", border:"#EDE9FE" },
  Weekly:  { from:"#0369A1", to:"#0891B2", soft:"#EFF6FF", border:"#DBEAFE" },
  Monthly: { from:"#BE185D", to:"#DB2777", soft:"#FDF2F8", border:"#FCE7F3" },
  Yearly:  { from:"#047857", to:"#059669", soft:"#ECFDF5", border:"#D1FAE5" },
};

/* period card → API param */
const PERIOD_MAP = {
  Daily:   "days",
  Weekly:  "weeks",
  Monthly: "months",
  Yearly:  "years",
};

const FREQ_TABS = [
  { key:"Daily",   icon:<Zap size={14}/>          },
  { key:"Weekly",  icon:<Repeat2 size={14}/>      },
  { key:"Monthly", icon:<CalendarDays size={14}/> },
  { key:"Yearly",  icon:<TrendingUp size={14}/>   },
];

/* ── Format date DD-MM-YYYY HH:MM ── */
function fmtDate(v) {
  if (!v) return "Not available";
  try {
    const d = new Date(v);
    if (isNaN(d)) return v;
    const dd = String(d.getDate()).padStart(2,"0");
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2,"0");
    const mi = String(d.getMinutes()).padStart(2,"0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  } catch { return "Not available"; }
}

/* ── Responsible name from array or string ── */
function getResponsible(task) {
  if (!task.assigned_to_name) return "Not assigned";
  if (Array.isArray(task.assigned_to_name))
    return task.assigned_to_name.filter(Boolean).join(", ") || "Not assigned";
  return task.assigned_to_name;
}

/* ── Status badge for task card ── */
function CardStatus({ task }) {
  const ok = task.is_active;
  const hasPending = !!task.next_occurrence;
  const label = ok ? "Active" : hasPending ? "Pending" : "Completed";
  const bg    = ok ? "#ECFDF5" : hasPending ? "#FFFBEB" : "#EFF6FF";
  const color = ok ? "#059669" : hasPending ? "#D97706" : "#0369A1";
  const bdr   = ok ? "#A7F3D0" : hasPending ? "#FDE68A" : "#BFDBFE";
  return (
    <span className="inline-flex items-center gap-[5px] text-[10px] font-bold px-2.5 py-1 rounded-lg border" style={{ background:bg, color, borderColor:bdr }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background:color }}/>
      {label}
    </span>
  );
}

/* ── Occurrence row status badge ── */
function OccBadge({ status, deadline }) {
  const now = new Date();
  const isOverdue = deadline && status !== "complete" && status !== "cancelled"
    && new Date(deadline) < now;

  let label = "Pending", bg = "#FFF3E0", color = "#F57C00", bdr = "#F57C00";
  if (isOverdue)           { label="Overdue";     bg="#FFEBEE"; color="#D32F2F"; bdr="#D32F2F"; }
  else if (status==="in progress") { label="In Progress"; bg="#E3F2FD"; color="#1976D2"; bdr="#1976D2"; }
  else if (status==="complete")    { label="Complete";    bg="#E8F5E9"; color="#388E3C"; bdr="#388E3C"; }
  else if (status==="cancelled")   { label="Cancelled";   bg="#FCE4EC"; color="#C2185B"; bdr="#C2185B"; }

  return (
    <span className="px-3 py-1.5 rounded-[20px] text-xs font-semibold uppercase inline-block min-w-[100px] border" style={{ background:bg, color, borderColor:bdr }}>
      {label}
    </span>
  );
}

/* ── Avatar ── */
const AV_BG = ["#6D28D9","#0369A1","#BE185D","#047857","#D97706","#DC2626","#7C3AED","#0891B2"];
function Av({ name, idx=0, size=28 }) {
  const ini = (name||"?").split(" ").slice(0,2).map(w=>w[0]||"").join("").toUpperCase();
  return (
    <span
      className="rounded-full text-white font-bold inline-flex items-center justify-center flex-shrink-0 shadow-[0_0_0_2px_#fff]"
      style={{ width:size, height:size, background:AV_BG[idx%AV_BG.length], fontSize:size*0.35 }}
    >
      {ini}
    </span>
  );
}

/* ── Responsive hook ── */
function useWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

/* ── Skeleton ── */
function SkeletonCard() {
  return (
    <div className="bg-white border border-[#E0E6ED] rounded-lg p-5 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-[15px] [animation:pulse_1.4s_ease-in-out_infinite]">
      <div className="flex justify-between mb-[15px] pb-[15px] border-b-2 border-[#F0F2F5]">
        <div className="h-3.5 w-[40%] bg-[#E2E8F0] rounded-md"/>
        <div className="h-[22px] w-[70px] bg-[#E2E8F0] rounded-xl"/>
      </div>
      <div className="grid gap-[15px] [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        {[1,2,3,4].map(i => (
          <div key={i}>
            <div className="h-2.5 w-1/2 bg-[#E2E8F0] rounded mb-1.5"/>
            <div className="h-3 w-4/5 bg-[#F1F5F9] rounded"/>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══ OCCURRENCE OFFCANVAS ════════════════════════════════════ */
function OccurrenceOffcanvas({ taskId, currentFreq, onClose }) {
  const [task,    setTask]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!taskId) return;
    setLoading(true); setError(null); setTask(null);
    axiosInstance.get(`/task_assignment/get_task_details/${taskId}`)
      .then(r => setTask(r.data))
      .catch(() => setError("Failed to load task details"))
      .finally(() => setLoading(false));
  }, [taskId]);

  const periodLabel = { Daily:"Daily", Weekly:"Weekly", Monthly:"Monthly", Yearly:"Yearly" }[currentFreq];
  const occurrences = task?.occurrence_dates || [];
  const completed   = occurrences.filter(o => o.status === "complete").length;
  const pending     = occurrences.filter(o => ["pending","in progress"].includes(o.status)).length;

  return (
    <div className="fixed inset-0 z-[9000] flex justify-end">
      {/* Backdrop */}
      <div onClick={onClose} className="absolute inset-0 bg-black/40"/>

      {/* Panel */}
      <div className="relative w-[min(70%,1200px)] h-full bg-white flex flex-col shadow-[-4px_0_30px_rgba(0,0,0,0.15)] [animation:slideIn_0.25s_ease]">

        {/* Close button — left pill */}
        <button onClick={onClose}
          className="absolute -left-12 top-5 rounded-l-[25px] z-10 px-3.5 py-[9px] bg-white border-none cursor-pointer text-[#677DE8]">
          <X size={20}/>
        </button>

        {/* Header */}
        <div className="bg-[linear-gradient(135deg,#667eea,#764ba2)] text-white p-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Repeat2 size={24}/>
            <div>
              <h5 className="m-0 font-bold">{task?.task_type || "Task Occurrences"}</h5>
              <small className="opacity-75">{periodLabel} Recurring Task</small>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="text-center py-10">
              <div className="w-12 h-12 border-4 border-[#E2E8F0] rounded-full [animation:spin_0.8s_linear_infinite] mx-auto mb-3" style={{ borderTopColor:"#667eea" }}/>
              <p className="text-[#94A3B8]">Loading occurrences...</p>
            </div>
          )}
          {error && <p className="text-[#DC2626] text-center py-10">{error}</p>}

          {task && !loading && (
            <>
              {/* Summary cards */}
              <div className="grid gap-4 mb-6 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {[
                  { icon:<CalendarDays size={20}/>, label:"Interval",    val:`Every ${task.repeat_interval||1} ${PERIOD_MAP[currentFreq]||"days"}`, c:"#6366F1" },
                  { icon:<User size={20}/>,         label:"Responsible", val:getResponsible(task), c:"#10B981" },
                  { icon:<Shield size={20}/>,       label:"Accountable", val:task.task_created_by_name||"Unknown", c:"#0EA5E9" },
                  { icon:<Hash size={20}/>,         label:"Total Occurrences", val:occurrences.length, c:"#F59E0B" },
                ].map((s,i) => (
                  <div key={i} className="bg-white border border-[#F1F5F9] rounded-[10px] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center gap-2 mb-2" style={{ color:s.c }}>
                      {s.icon}
                      <span className="text-xs text-[#6C757D] font-semibold">{s.label}</span>
                    </div>
                    <p className="m-0 font-bold text-sm text-[#2C3E50]">{s.val}</p>
                  </div>
                ))}
              </div>

              {/* Stats bar */}
              <div className="bg-[#F8F9FA] border border-[#E9ECEF] rounded-[10px] px-6 py-4 mb-6">
                <div className="grid grid-cols-3 text-center">
                  {[
                    { label:"Total",     val:occurrences.length, color:"#1976D2" },
                    { label:"Completed", val:completed,          color:"#388E3C" },
                    { label:"Pending",   val:pending,            color:"#F57C00" },
                  ].map((s,i) => (
                    <div key={i} className="flex items-center justify-center gap-2">
                      <div>
                        <div className="text-xs text-[#6C757D]">{s.label}</div>
                        <div className="text-[22px] font-bold" style={{ color:s.color }}>{s.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Occurrences table */}
              {occurrences.length === 0 ? (
                <div className="text-center py-[60px] px-5">
                  <p className="text-[#94A3B8] text-sm">No occurrences found for this task</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[calc(100vh-450px)] overflow-y-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        {["#","Created Date","Start Date","Deadline Date","Completed Date","Status"].map((h,i) => (
                          <th key={i} className="bg-[linear-gradient(135deg,#667eea,#764ba2)] text-white font-semibold text-center px-2 py-3 border-none sticky top-0 z-10 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {occurrences.map((occ, idx) => (
                        <tr key={idx} className={`transition-colors duration-150 hover:bg-[#F8F9FA] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"}`}>
                          <td className="text-center px-2 py-2.5 font-bold">{idx+1}</td>
                          <td className="px-2 py-2.5 text-center">{fmtDate(occ.task_created_at)}</td>
                          <td className="px-2 py-2.5 text-center">{fmtDate(occ.start_date)}</td>
                          <td className="px-2 py-2.5 text-center">{fmtDate(occ.completion_sla)}</td>
                          <td className="px-2 py-2.5 text-center">{fmtDate(occ.completed_at)}</td>
                          <td className="text-center px-2 py-2.5">
                            <OccBadge status={occ.status||"pending"} deadline={occ.completion_sla}/>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
        @keyframes spin { to { transform:rotate(360deg) } }
      `}</style>
    </div>
  );
}

/* ══ MAIN COMPONENT ══════════════════════════════════════════ */
export default function StandingTask() {
  /* ── data ── */
  const [counts,   setCounts]   = useState({ Daily:0, Weekly:0, Monthly:0, Yearly:0 });
  const [tasks,    setTasks]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /* ── UI ── */
  const [freq,         setFreq]         = useState("Daily");
  const [page,         setPage]         = useState(1);
  const [perPage,      setPerPage]      = useState(10);
  const [search,       setSearch]       = useState("");
  const [offcanvasId,  setOffcanvasId]  = useState(null); // task _id for offcanvas

  const w = useWidth();
  const isMobile = w < 640;
  const c = COLORS[freq];

  /* ════ LOAD COUNTS — GET /task_assignment/get_recurring_tasks_count?period=days ════ */
  const loadAllCounts = useCallback(async () => {
    const periods = ["days","weeks","months","years"];
    const keys    = ["Daily","Weekly","Monthly","Yearly"];
    const results = await Promise.allSettled(
      periods.map(p => axiosInstance.get("/task_assignment/get_recurring_tasks_count", { params:{ period:p } }))
    );
    const updated = { Daily:0, Weekly:0, Monthly:0, Yearly:0 };
    results.forEach((r, i) => {
      if (r.status === "fulfilled" && r.value.data?.success) {
        updated[keys[i]] = r.value.data.count || 0;
      }
    });
    setCounts(updated);
  }, []);

  /* ════ LOAD TASKS — GET /task_assignment/get_recurring_tasks?period=days ════ */
  const loadTasks = useCallback(async (freqKey, isRefresh=false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setTasks([]);
    setPage(1);

    try {
      const period = PERIOD_MAP[freqKey];
      const res = await axiosInstance.get("/task_assignment/get_recurring_tasks", {
        params: { period }
      });
      if (res.data?.success && Array.isArray(res.data.tasks)) {
        setTasks(res.data.tasks);
      } else {
        setTasks([]);
      }
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /* initial load */
  useEffect(() => {
    loadAllCounts();
    loadTasks("Daily");
  }, [loadAllCounts, loadTasks]);

  /* freq tab switch */
  const handleFreqChange = (key) => {
    setFreq(key);
    setSearch("");
    setPage(1);
    loadTasks(key);
    axiosInstance.get("/task_assignment/get_recurring_tasks_count", { params:{ period:PERIOD_MAP[key] } })
      .then(r => { if (r.data?.success) setCounts(prev => ({ ...prev, [key]: r.data.count||0 })); })
      .catch(() => {});
  };

  /* refresh button */
  const handleRefresh = () => {
    loadAllCounts();
    loadTasks(freq, true);
  };

  /* filtered + paginated */
  const filtered = tasks.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.task_type||"").toLowerCase().includes(q) ||
           getResponsible(t).toLowerCase().includes(q);
  });
  const total   = Math.max(1, Math.ceil(filtered.length / perPage));
  const visible = filtered.slice((page-1)*perPage, page*perPage);

  /* ── Task card click → open offcanvas ── */
  const handleTaskClick = (taskId) => setOffcanvasId(taskId);

  /* ── Mobile Card ── */
  const MobileCard = ({ t, i }) => (
    <div
      onClick={() => handleTaskClick(t._id)}
      className="bg-white border border-[#E0E6ED] rounded-lg p-3.5 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-2 cursor-pointer transition-all duration-300 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-0.5 border-l-[3px]"
      style={{ borderLeftColor:c.from }}
    >
      <div className="flex justify-between items-start mb-2.5">
        <p className="text-[13px] font-bold text-[#2C3E50] m-0 flex-1">{t.task_type||"Untitled"}</p>
        <CardStatus task={t}/>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <InfoBox label="Next"     val={fmtDate(t.next_occurrence)} color={c.from}/>
        <InfoBox label="Interval" val={`Every ${t.repeat_interval||1} ${PERIOD_MAP[freq]}`}/>
        <InfoBox label="Responsible" val={getResponsible(t)} avatar={getResponsible(t)} idx={i}/>
        <InfoBox label="Accountable" val={t.task_created_by_name||"—"} avatar={t.task_created_by_name} idx={i+4}/>
      </div>
    </div>
  );

  /* ── Desktop Row ── */
  const DesktopRow = ({ t, i }) => (
    <div
      onClick={() => handleTaskClick(t._id)}
      className="grid items-center border-b border-[#F8FAFC] cursor-pointer transition-colors duration-100 hover:bg-[#EEF2FF] [grid-template-columns:1fr_120px_155px_155px_1fr_1fr_96px]"
      style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}
    >

      {/* Task */}
      <div className="px-3.5 py-3 flex items-center gap-2.5 min-w-0">
        <div className="w-[3px] h-10 rounded flex-shrink-0" style={{ background:`linear-gradient(180deg,${c.from},${c.to})` }}/>
        <p className="text-[13px] font-bold text-[#2C3E50] m-0 overflow-hidden text-ellipsis whitespace-nowrap">
          {t.task_type||"Untitled"}
        </p>
      </div>

      {/* Interval */}
      <div className="px-3.5 py-3">
        <span className="text-[11px] text-[#64748B] font-semibold">
          Every {t.repeat_interval||1} {PERIOD_MAP[freq]}
        </span>
      </div>

      {/* Next */}
      <div className="px-3.5 py-3">
        <span className="text-[11px] font-bold" style={{ color:c.from }}>
          {fmtDate(t.next_occurrence)}
        </span>
      </div>

      {/* Last */}
      <div className="px-3.5 py-3">
        <span className="text-[11px] text-[#94A3B8]">
          {fmtDate(t.last_occurrence)}
        </span>
      </div>

      {/* Responsible */}
      <div className="px-3.5 py-3 flex items-center gap-2 min-w-0">
        <Av name={getResponsible(t)} idx={i}/>
        <span className="text-[11px] font-bold text-[#334155] overflow-hidden text-ellipsis whitespace-nowrap">
          {getResponsible(t)}
        </span>
      </div>

      {/* Accountable */}
      <div className="px-3.5 py-3 flex items-center gap-2 min-w-0">
        <Av name={t.task_created_by_name||"?"} idx={i+4}/>
        <span className="text-[11px] font-bold text-[#334155] overflow-hidden text-ellipsis whitespace-nowrap">
          {t.task_created_by_name||"—"}
        </span>
      </div>

      {/* Status */}
      <div className="px-3.5 py-3">
        <CardStatus task={t}/>
      </div>
    </div>
  );

  /* ════ RENDER ════ */
  return (
    <div className={`bg-[#F8FAFC] rounded-2xl min-w-0 ${isMobile ? "p-3" : "p-4"}`}>

      {/* ── HERO ── */}
      <div className="rounded-[14px] overflow-hidden bg-[linear-gradient(135deg,#1E1B4B_0%,#312E81_60%,#4338CA_100%)] mb-3 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(167,139,250,0.12),transparent_65%)] pointer-events-none"/>
        <div className={`relative z-[1] flex items-center justify-between flex-wrap gap-3 ${isMobile ? "px-4 py-3.5" : "px-6 py-4"}`}>
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/[.15] flex items-center justify-center">
              <Repeat2 size={18} color="#A5B4FC"/>
            </div>
            <div>
              <p className="text-[9px] font-bold text-[#A5B4FC] tracking-[.15em] uppercase m-0">Standing Tasks</p>
              <h1 className={`font-black text-white mt-0.5 mb-0 tracking-[-0.3px] ${isMobile ? "text-[15px]" : "text-lg"}`}>Recurring Task Dashboard</h1>
            </div>
          </div>
          <div className={`flex items-center ${isMobile ? "gap-3" : "gap-5"}`}>
            <div className="w-px h-8 bg-white/10"/>
            {[
              { label:"Total",  val: Object.values(counts).reduce((a,b)=>a+b,0) },
              { label:"Daily",  val: counts.Daily  },
              { label:"Weekly", val: counts.Weekly },
            ].map((s,i) => (
              <div key={i} className="text-center">
                <p className={`font-black text-white m-0 leading-none ${isMobile ? "text-lg" : "text-[22px]"}`}>{s.val}</p>
                <p className="text-[8px] font-bold text-white/40 tracking-[.12em] uppercase mt-[3px] mb-0">{s.label}</p>
              </div>
            ))}
            <button
              onClick={handleRefresh}
              disabled={refreshing||loading}
              className="w-8 h-8 rounded-lg bg-white/10 border border-white/[.15] flex items-center justify-center cursor-pointer"
            >
              <RefreshCw
                size={14}
                color="#A5B4FC"
                className={`transition-transform duration-[600ms] ease-in-out ${refreshing ? "rotate-[360deg]" : "rotate-0"}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* ── FREQ TABS ── */}
      <div className={`grid gap-2.5 mb-3 ${isMobile ? "grid-cols-2" : "grid-cols-4"}`}>
        {FREQ_TABS.map(tab => {
          const tc     = COLORS[tab.key];
          const active = freq === tab.key;
          const cnt    = counts[tab.key];
          const pct    = Math.round(cnt / Math.max(Object.values(counts).reduce((a,b)=>a+b,0),1) * 100);
          return (
            <button
              key={tab.key}
              onClick={() => handleFreqChange(tab.key)}
              className={`rounded-xl cursor-pointer text-left transition-all duration-150 border-[1.5px] ${isMobile ? "px-3 py-2.5" : "px-4 py-3"}`}
              style={{
                background: active ? `linear-gradient(135deg,${tc.from},${tc.to})` : "#fff",
                borderColor: active ? tc.from : tc.border,
                boxShadow: active ? `0 4px 16px ${tc.from}30` : "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div className="flex items-start justify-between mb-2.5">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: active ? "rgba(255,255,255,0.18)" : tc.soft, color: active ? "#fff" : tc.from }}
                >
                  {tab.icon}
                </div>
                <div
                  className="rounded-[10px] min-w-[48px] px-2.5 py-1 text-center border-[1.5px]"
                  style={{ background: active ? "rgba(255,255,255,0.18)" : tc.soft, borderColor: active ? "rgba(255,255,255,0.28)" : tc.border }}
                >
                  <p className="text-[22px] font-black m-0 leading-none tracking-[-0.5px]" style={{ color: active ? "#fff" : tc.from }}>{cnt}</p>
                  <p className="text-[8px] font-bold uppercase tracking-[.08em] mt-0.5 mb-0" style={{ color: active ? "rgba(255,255,255,0.55)" : "#94A3B8" }}>tasks</p>
                </div>
              </div>
              <p className={`font-black uppercase tracking-[.08em] m-0 ${isMobile ? "text-[11px]" : "text-[13px]"}`} style={{ color: active ? "#fff" : tc.from }}>{tab.key}</p>
              <p className="text-[10px] mt-0.5 mb-2" style={{ color: active ? "rgba(255,255,255,0.5)" : "#94A3B8" }}>
                Recurring tasks
              </p>
              <div className="h-[3px] rounded" style={{ background: active ? "rgba(255,255,255,0.2)" : tc.border }}>
                <div className="h-full rounded transition-[width] duration-[400ms] ease-in-out" style={{ width:`${pct}%`, background: active ? "#fff" : tc.from }}/>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── SEARCH ── */}
      <div className="flex flex-wrap gap-2 mb-2.5 items-center">
        <div className={`relative flex-1 ${isMobile ? "min-w-full" : "min-w-[200px]"}`}>
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
          <input
            value={search}
            onChange={e=>{setSearch(e.target.value);setPage(1);}}
            placeholder="Search tasks…"
            className="w-full pl-[30px] pr-3 py-2 text-xs font-medium text-[#334155] bg-white border-[1.5px] border-[#E2E8F0] rounded-[10px] outline-none box-border"
          />
        </div>
        {!loading && (
          <span className="text-[11px] text-[#94A3B8] font-semibold whitespace-nowrap">
            {filtered.length} result{filtered.length!==1?"s":""}
          </span>
        )}
      </div>

      {/* ── TASK LIST ── */}
      <div className="bg-white rounded-lg border border-[#E0E6ED] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.05)]">

        {isMobile ? (
          <div className="px-2.5 pt-2.5 pb-1">
            {loading
              ? [1,2,3].map(i=><SkeletonCard key={i}/>)
              : visible.length===0
                ? <EmptyState/>
                : visible.map((t,i)=><MobileCard key={t._id||i} t={t} i={i}/>)
            }
          </div>
        ) : (
          <>
            {/* thead */}
            <div className="grid bg-[#F8FAFC] border-b border-[#F1F5F9] [grid-template-columns:1fr_120px_155px_155px_1fr_1fr_96px]">
              {["Task","Interval","Next Occurrence","Last Occurrence","Responsible","Accountable","Status"].map((h,i)=>(
                <div key={i} className="px-3.5 py-2.5 text-[10px] font-extrabold text-[#94A3B8] uppercase tracking-[.1em]">{h}</div>
              ))}
            </div>

            {loading
              ? Array.from({length:5}).map((_,i)=>(
                  <div key={i} className="grid border-b border-[#F8FAFC] py-3 [animation:pulse_1.4s_ease-in-out_infinite] [grid-template-columns:1fr_120px_155px_155px_1fr_1fr_96px]">
                    {[1,2,3,4,5,6,7].map(j=>(
                      <div key={j} className="mx-3.5 h-3.5 bg-[#E2E8F0] rounded-md"/>
                    ))}
                  </div>
                ))
              : visible.length===0
                ? <EmptyState/>
                : visible.map((t,i)=><DesktopRow key={t._id||i} t={t} i={i}/>)
            }
          </>
        )}

        {/* ── PAGINATION ── */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 border-t border-[#F1F5F9] bg-white">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[#94A3B8]">Items per page:</span>
            <div className="relative">
              <select
                value={perPage}
                onChange={e=>{setPerPage(Number(e.target.value));setPage(1);}}
                className="appearance-none text-[11px] font-bold text-[#334155] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg py-[5px] pr-6 pl-2.5 outline-none cursor-pointer"
              >
                {[10,20,50,100].map(n=><option key={n}>{n}</option>)}
              </select>
              <ChevronDown size={10} className="absolute right-[7px] top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
            </div>
            {!isMobile && !loading && (
              <span className="text-[11px] text-[#94A3B8]">
                Showing <b className="text-[#475569]">
                  {Math.min((page-1)*perPage+1,filtered.length)}–{Math.min(page*perPage,filtered.length)}
                </b> of <b className="text-[#475569]">{filtered.length}</b>
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <PgBtn onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}><ChevronLeft size={12}/></PgBtn>
            {Array.from({length:total},(_,i)=>i+1)
              .filter(n=>Math.abs(n-page)<=1||n===1||n===total)
              .reduce((acc,n,i,arr)=>{ if(i>0&&n-arr[i-1]>1) acc.push("…"); acc.push(n); return acc; },[])
              .map((n,i)=>n==="…"
                ? <span key={`e${i}`} className="w-7 h-7 flex items-center justify-center text-[11px] text-[#94A3B8]">…</span>
                : <PgBtn key={n} active={page===n} activeColor={c.from}
                    onClick={()=>setPage(n)}>{n}</PgBtn>
              )}
            <PgBtn onClick={()=>setPage(p=>Math.min(total,p+1))} disabled={page===total}><ChevronRight size={12}/></PgBtn>
          </div>
        </div>
      </div>

      {/* ── OCCURRENCE OFFCANVAS ── */}
      {offcanvasId && (
        <OccurrenceOffcanvas
          taskId={offcanvasId}
          currentFreq={freq}
          onClose={()=>setOffcanvasId(null)}
        />
      )}
    </div>
  );
}

/* ── Small helpers ── */
function InfoBox({ label, val, color, avatar, idx=0 }) {
  return (
    <div className="bg-[#F8FAFC] rounded-lg px-2 py-1.5">
      <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-[.06em] m-0">{label}</p>
      {avatar ? (
        <div className="flex items-center gap-1.5 mt-0.5">
          <Av name={avatar} idx={idx} size={18}/>
          <p className="text-[10px] font-bold text-[#334155] m-0 overflow-hidden text-ellipsis whitespace-nowrap">{val}</p>
        </div>
      ) : (
        <p className="text-[10px] font-bold mt-0.5 mb-0" style={{ color:color||"#475569" }}>{val||"—"}</p>
      )}
    </div>
  );
}

function PgBtn({ onClick, disabled, active, activeColor, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-7 h-7 flex items-center justify-center rounded-lg border text-[11px] font-bold transition-all duration-150 ${
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer opacity-100"
      }`}
      style={{
        background:  active ? activeColor : "#fff",
        borderColor: active ? activeColor : "#E2E8F0",
        color:       active ? "#fff" : disabled ? "#CBD5E1" : "#64748B",
      }}
    >
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-14 text-[#94A3B8] text-[13px]">
      No recurring tasks found for this period.
    </div>
  );
}